/* AUTO-SPLIT from Documents.jsx (Iteration 14).
 * Per-path document file. Shared primitives are imported from ./_shared.
 * Do not edit Documents.jsx for these components — edit here.
 */
import React from "react";
import { Landmark } from "lucide-react";
import { fmtCurrency, fmtDate, numberToWords } from "../../lib/format";
import { calc, DocFooter, Paper, hashStr, makeSerial, TreasuryLogo, BarCode, BankersAcceptance } from "./_shared";

export const Doc1MasterNote = React.forwardRef(({ d }, ref) => {
  const principalWords = numberToWords(d.loanPrincipal);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc 01 — Master Credit Facility Promissory Note" />}>
      <h2 className="doc-sub">Document 01 · Commercial Liquidity & Financing Package</h2>
      <h1 className="doc-title">Master Credit Facility Promissory Note</h1>
      <hr className="doc-rule" />

      <div className="principal-line">
        <span>{fmtCurrency(d.loanPrincipal)}</span>
        <span>{d.borrowerCity}, {d.borrowerState}</span>
      </div>

      <table className="kv-table">
        <tbody>
          <tr><td>Execution Date</td><td>{fmtDate(d.executionDate)}</td></tr>
          <tr><td>Maturity Date</td><td>{fmtDate(d.maturityDate)}</td></tr>
          <tr><td>Fixed Interest Rate</td><td>{d.loanRate.toFixed(2)}% per annum (365-day basis)</td></tr>
          <tr><td>Governing Law</td><td>State of {d.governingLaw}</td></tr>
        </tbody>
      </table>

      <ol>
        <li>
          <span className="clause-title">Promise to Pay.</span> For value received, the undersigned <strong>{d.borrowerName}</strong>, a {d.borrowerState} limited liability company (the <em>"Borrower"</em>), hereby promises to pay to the order of <strong>{d.lenderName}</strong> (the <em>"Lender"</em>), at its offices in {d.borrowerCity}, {d.borrowerState}, the principal sum of <span className="legal-words">{principalWords.toUpperCase()}</span> <strong>({fmtCurrency(d.loanPrincipal)})</strong>, together with interest accrued on the unpaid principal balance at a fixed rate of <strong>{d.loanRate.toFixed(2)}% per annum</strong>, calculated on the basis of a 365-day year.
        </li>
        <li>
          <span className="clause-title">Maturity and Repayment.</span> All principal and accrued interest shall be due and payable in full on <strong>{fmtDate(d.maturityDate)}</strong> (the <em>"Maturity Date"</em>). All payments hereunder shall be made in lawful money of the United States of America by wire transfer of immediately available funds.
        </li>
        <li>
          <span className="clause-title">Security.</span> This Note is a secured commercial obligation and is entitled to the benefits of, and security interests granted under, a contemporaneous Commercial Security and Pledge Agreement of even date herewith, executed by Borrower in favor of Lender, covering a certain third-party negotiable instrument as more particularly described therein.
        </li>
        <li>
          <span className="clause-title">Default Remedies.</span> Upon the occurrence of any Event of Default under this Note or the accompanying Security Agreement, the entire unpaid principal balance and all accrued interest shall, at the option of the Lender, become immediately due and payable without notice, presentment, or demand, each of which is hereby expressly waived by Borrower.
        </li>
        <li>
          <span className="clause-title">Governing Law &amp; Venue.</span> This Note shall be governed by and construed in accordance with the laws of the State of {d.governingLaw}. Exclusive venue for any action arising hereunder shall lie in {d.jurisdiction}.
        </li>
      </ol>

      <div className="sig-block">
        <div className="sig-col">
          <p><strong>BORROWER:</strong></p>
          <p>{d.borrowerName}</p>
          <div className="sig-line">By: {d.signerName}</div>
          <div className="sig-line">Title: {d.signerTitle}</div>
        </div>
        <div className="sig-col">
          <p><strong>LENDER (Acknowledged):</strong></p>
          <p>{d.lenderName}</p>
          <div className="sig-line">By: {d.lenderOfficer}</div>
          <div className="sig-line">Title: Commercial Loan Officer</div>
        </div>
      </div>
    </Paper>
  );
});

// ============ DOCUMENT 2: Commercial Security and Pledge Agreement ============

export const Doc2Pledge = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 02 — Commercial Security and Pledge Agreement" />}>
    <h2 className="doc-sub">Document 02 · Commercial Liquidity & Financing Package</h2>
    <h1 className="doc-title">Commercial Security &amp; Pledge Agreement</h1>
    <hr className="doc-rule" />

    <p>
      This Commercial Security and Pledge Agreement (the <em>"Agreement"</em>) is entered into as of <strong>{fmtDate(d.executionDate)}</strong>, by and between <strong>{d.borrowerName}</strong> (<em>"Pledgor"</em>) and <strong>{d.lenderName}</strong> (<em>"Lender"</em>).
    </p>

    <ol>
      <li>
        <span className="clause-title">Grant of Security Interest.</span> Pledgor hereby transfers, pledges, and grants to Lender a continuing, first-priority security interest in, and lien upon, all of Pledgor's right, title, and interest in the following property (the <em>"Collateral"</em>):
        <ol type="a" style={{ marginTop: 8 }}>
          <li>That certain UCC Article 3 Promissory Note dated <strong>{fmtDate(d.executionDate)}</strong>, executed by <strong>{d.makerName}</strong> (<em>"Maker"</em>) payable to the order of Pledgor in the face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong> bearing fixed interest at {d.couponRate.toFixed(2)}% per annum and maturing on {fmtDate(d.maturityDate)} (the <em>"Pledged Note"</em>); and</li>
          <li>All cash, interest, principal payments, and proceeds now or hereafter due or received under or in connection with the Pledged Note, including all substitutions, renewals, and modifications thereof.</li>
        </ol>
      </li>
      <li>
        <span className="clause-title">Perfecting via Possession.</span> Concurrently with the execution of this Agreement, Pledgor shall physically deliver the original, wet-ink signed Pledged Note to Lender. Pledgor acknowledges and agrees that Lender's security interest is perfected by physical possession pursuant to Section 9-313 of the Uniform Commercial Code.
      </li>
      <li>
        <span className="clause-title">Recourse and Default.</span> This pledge is made with <strong>full recourse</strong> to Pledgor. If the Maker of the Pledged Note defaults on its obligation at maturity, Pledgor remains fully liable to satisfy the underlying {fmtCurrency(d.loanPrincipal)} loan obligation to Lender under the Master Credit Facility Promissory Note.
      </li>
      <li>
        <span className="clause-title">Representations.</span> Pledgor warrants that (a) Pledgor is the sole legal and beneficial owner of the Pledged Note, free and clear of any liens; (b) the Pledged Note is genuine and enforceable; and (c) no portion of the Pledged Note has been assigned, sold, or otherwise hypothecated.
      </li>
      <li>
        <span className="clause-title">Governing Law.</span> This Agreement shall be governed by the laws of the State of {d.governingLaw}.
      </li>
    </ol>

    <div className="sig-block">
      <div className="sig-col">
        <p><strong>PLEDGOR:</strong></p>
        <p>{d.borrowerName}</p>
        <div className="sig-line">By: {d.signerName}</div>
        <div className="sig-line">Title: {d.signerTitle}</div>
      </div>
      <div className="sig-col">
        <p><strong>LENDER:</strong></p>
        <p>{d.lenderName}</p>
        <div className="sig-line">By: {d.lenderOfficer}</div>
        <div className="sig-line">Title: Commercial Loan Officer</div>
      </div>
    </div>
  </Paper>
));

// ============ DOCUMENT 3: UCC-1 Financing Statement ============

export const Doc3UCC = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 03 — UCC-1 Financing Statement (Collateral Description)" />}>
    <h2 className="doc-sub">Document 03 · Commercial Liquidity & Financing Package</h2>
    <h1 className="doc-title">UCC-1 Financing Statement</h1>
    <h2 className="doc-sub">Collateral Description Attachment</h2>
    <hr className="doc-rule" />

    <table className="kv-table">
      <tbody>
        <tr><td>Debtor</td><td>{d.borrowerName}</td></tr>
        <tr><td>Debtor Address</td><td>{d.borrowerAddress}</td></tr>
        <tr><td>Secured Party</td><td>{d.lenderName}</td></tr>
        <tr><td>Secured Party Address</td><td>{d.lenderAddress}</td></tr>
        <tr><td>Filing Jurisdiction</td><td>Secretary of State, State of {d.governingLaw}</td></tr>
        <tr><td>Filing Date</td><td>{fmtDate(d.executionDate)}</td></tr>
      </tbody>
    </table>

    <p style={{ marginTop: 18, fontWeight: 600 }}>Collateral Covered by this Financing Statement:</p>

    <p>
      A continuing, first-priority security interest in that certain negotiable Promissory Note dated <strong>{fmtDate(d.executionDate)}</strong>, executed by <strong>{d.makerName}</strong> (<em>"Maker"</em>) and payable to the order of Debtor in the original face principal amount of <span className="legal-words">{numberToWords(d.faceValue).toUpperCase()}</span> <strong>({fmtCurrency(d.faceValue)})</strong>, bearing a fixed annual interest rate of <strong>{d.couponRate.toFixed(2)}%</strong> and maturing on <strong>{fmtDate(d.maturityDate)}</strong>, together with any and all extensions, renewals, modifications, or substitutions thereof; and all instruments, accounts, contract rights, chattel paper, general intangibles, and cash or non-cash proceeds arising from or relating to the collection, enforcement, or settlement of said Promissory Note.
    </p>

    <p>
      The original physical instrument has been delivered to, and is held in possession by, the Secured Party pursuant to possessory perfection under <strong>UCC § 9-313</strong>.
    </p>

    <p style={{ marginTop: 24, fontStyle: "italic", color: "#5C5C5C" }}>
      This collateral description shall be attached to and incorporated within the UCC-1 Financing Statement filed with the Office of the Secretary of State of {d.governingLaw}.
    </p>

    <div className="sig-block">
      <div className="sig-col">
        <p><strong>DEBTOR AUTHORIZATION:</strong></p>
        <p>{d.borrowerName}</p>
        <div className="sig-line">By: {d.signerName}</div>
        <div className="sig-line">Title: {d.signerTitle}</div>
      </div>
      <div className="sig-col">
        <p><strong>SECURED PARTY:</strong></p>
        <p>{d.lenderName}</p>
        <div className="sig-line">Filing Authorized By: {d.lenderOfficer}</div>
        <div className="sig-line">Date: {fmtDate(d.executionDate)}</div>
      </div>
    </div>
  </Paper>
));

// ============ DOCUMENT 4: Notice of Pledge and Direction of Payments ============

export const Doc4Notice = React.forwardRef(({ d }, ref) => {
  const { maturity } = calc(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc 04 — Notice of Pledge & Direction of Payments" />}>
      <h2 className="doc-sub">Document 04 · Commercial Liquidity & Financing Package</h2>
      <h1 className="doc-title">Notice of Pledge &amp; Direction of Payments</h1>
      <hr className="doc-rule" />

      <p><strong>Date:</strong> {fmtDate(d.executionDate)}</p>
      <p><strong>To:</strong> {d.makerName}</p>
      <p><strong>Address:</strong> {d.makerAddress}</p>
      <p style={{ marginTop: 14 }}><strong>Re:</strong> Notice of Collateral Pledge of Promissory Note dated {fmtDate(d.executionDate)}</p>

      <hr className="doc-rule-thin" />

      <p>
        Please take notice that <strong>{d.borrowerName}</strong> (<em>"Payee"</em>) has granted a security interest in, and has physically pledged and delivered as collateral, that certain Promissory Note executed by you dated <strong>{fmtDate(d.executionDate)}</strong>, in the face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong> (the <em>"Note"</em>), to <strong>{d.lenderName}</strong>, as Secured Party (<em>"Lender"</em>).
      </p>

      <p>
        In accordance with <strong>Section 9-406 of the Uniform Commercial Code</strong>, you are hereby authorized and directed to make the full maturity payment of <span className="legal-words">{numberToWords(maturity).toUpperCase()}</span> <strong>({fmtCurrency(maturity)})</strong> due on {fmtDate(d.maturityDate)} exclusively via wire transfer to the locked bank account managed by Lender:
      </p>

      <table className="kv-table">
        <tbody>
          <tr><td>Bank Name</td><td>{d.lenderBankName}</td></tr>
          <tr><td>ABA Routing Number</td><td>{d.lenderRouting}</td></tr>
          <tr><td>Account Number</td><td>{d.lenderAccount}</td></tr>
          <tr><td>Account Name</td><td>{d.lenderAccountRef}</td></tr>
        </tbody>
      </table>

      <p style={{ fontWeight: 600 }}>
        Payment made to any party other than the Lender account designated above shall not discharge your obligation under the Note.
      </p>

      <div className="sig-block">
        <div className="sig-col">
          <p><strong>PAYEE / PLEDGOR:</strong></p>
          <p>{d.borrowerName}</p>
          <div className="sig-line">By: {d.signerName}</div>
          <div className="sig-line">Title: {d.signerTitle}</div>
        </div>
        <div className="sig-col">
          <p><strong>ACKNOWLEDGED BY MAKER:</strong></p>
          <p>{d.makerName}</p>
          <div className="sig-line">By: ______________________</div>
          <div className="sig-line">Date of Receipt: ___________</div>
        </div>
      </div>
    </Paper>
  );
});

// ============ DOCUMENT 5: Corporate Resolution ============

export const Doc5Resolution = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 05 — Corporate Resolution Authorizing Hypothecation" />}>
    <h2 className="doc-sub">Document 05 · Commercial Liquidity & Financing Package</h2>
    <h1 className="doc-title">Corporate Resolution</h1>
    <h2 className="doc-sub">Authorizing Hypothecation of Note Collateral</h2>
    <hr className="doc-rule" />

    <p>
      At a duly convened meeting of the management of <strong>{d.borrowerName}</strong> (the <em>"Entity"</em>) held on {fmtDate(d.executionDate)}, with quorum present and acting throughout, the following resolutions were adopted and entered into the official records of the Entity:
    </p>

    <p>
      <strong>RESOLVED,</strong> that the Entity is authorized to obtain a commercial credit facility from <strong>{d.lenderName}</strong> (<em>"Lender"</em>) in the principal sum of <span className="legal-words">{numberToWords(d.loanPrincipal).toUpperCase()}</span> <strong>({fmtCurrency(d.loanPrincipal)})</strong> under the terms of a Master Credit Facility Promissory Note bearing interest at {d.loanRate.toFixed(2)}% per annum and maturing on {fmtDate(d.maturityDate)}; and
    </p>

    <p>
      <strong>FURTHER RESOLVED,</strong> that to secure said credit facility, the Entity is fully authorized to pledge, hypothecate, and deliver as collateral its asset consisting of a third-party {fmtCurrency(d.faceValue)} Promissory Note dated {fmtDate(d.executionDate)}, issued by <strong>{d.makerName}</strong>; and
    </p>

    <p>
      <strong>FURTHER RESOLVED,</strong> that <strong>{d.signerName}</strong>, in their official capacity as {d.signerTitle}, is hereby empowered, authorized, and directed on behalf of this Entity to execute and deliver the Master Note, the Commercial Security and Pledge Agreement, the UCC-1 Filings, and the Payment Redirection Notices, and to take all necessary and appropriate steps to consummate the financing transaction.
    </p>

    <hr className="doc-rule" />

    <p style={{ textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, fontSize: "11pt" }}>Certification</p>

    <p>
      The undersigned, in their capacity as Secretary of {d.borrowerName}, hereby certifies that the foregoing is a true and exact copy of resolutions duly adopted by the management of the Entity on {fmtDate(d.executionDate)}, and that said resolutions remain in full force and effect as of the date hereof and have not been amended, modified, or rescinded.
    </p>

    <div className="sig-block">
      <div className="sig-col">
        <p><strong>SECRETARY / MANAGING PARTNER:</strong></p>
        <p>{d.borrowerName}</p>
        <div className="sig-line">By: {d.secretaryName}</div>
        <div className="sig-line">Title: Secretary</div>
      </div>
      <div className="sig-col">
        <p><strong>TREASURY HOUSE · PAYING AGENT:</strong></p>
        <div style={{ marginTop: 10 }}>
          <TreasuryLogo name={d.treasuryHouseName} tagline="Authorized Acceptance Desk" />
        </div>
        <div className="sig-line" style={{ marginTop: 30 }}>By: {d.acceptanceOfficer}</div>
        <div className="sig-line">Title: {d.acceptanceOfficerTitle}</div>
        <div className="sig-line">Bank: {d.acceptanceBankName}</div>
      </div>
    </div>
  </Paper>
));

// ============ DOCUMENT 6: Note Purchase & Assignment Agreement (DISCOUNT SALE) ============

